import {useCallback, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {BASE_URL} from '../../../api'
import config from '../../config'
const {axios} = config

// Roadmap #24 (2026-09-28): practitioner text is tidied automatically when
// saved (ALL CAPS, stray spaces, missing capitals). Anything containing a
// listed word shows up here for review - nothing is ever blocked.
const KIND = {profile: 'Profile', service: 'Service', class: 'Class', review: 'Review'}
const FIELD = {
  firstName: 'First name',
  lastName: 'Last name',
  businessName: 'Business name',
  bio: 'Bio',
  bioEs: 'Bio (Spanish)',
  specialty: 'Specialty',
  serviceName: 'Service name',
  desc: 'Description',
  name: 'Class name',
  description: 'Description',
  text: 'Review text',
  reply: "Practitioner's reply",
}
// AI content check (roadmap #17.8)
const AI_CATEGORY = {
  medical_claim: 'Medical claim',
  off_platform: 'Booking/paying outside the app',
  spam: 'Spam',
  offensive: 'Offensive',
  misleading: 'Misleading',
}
const removeLabel = (r) => (r.kind === 'review' ? (r.field === 'reply' ? 'Remove reply' : 'Hide review') : 'Remove text')
const NAME_FIELDS = ['firstName', 'lastName', 'serviceName', 'name']

const highlight = (text, words) => {
  if (!text || !words?.length) return text
  const re = new RegExp(`(^|[^\\p{L}])(${words.join('|')})(?=$|[^\\p{L}])`, 'giu')
  const out = []
  let last = 0
  for (const m of text.matchAll(re)) {
    const start = m.index + m[1].length
    out.push(text.slice(last, start))
    out.push(
      <mark key={start} style={{background: '#ffd6d6', padding: '0 2px'}}>
        {m[2]}
      </mark>
    )
    last = start + m[2].length
  }
  out.push(text.slice(last))
  return out
}

const FlaggedContent = () => {
  const [rows, setRows] = useState([])
  const [status, setStatus] = useState('open')
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const [scanning, setScanning] = useState(false)
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/content-flags`, {params: {status}})
      setRows(data.data || [])
    } catch (e) {
      setError('Could not load flagged content.')
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => {
    load()
  }, [load])

  const act = async (r, action) => {
    if (
      action === 'clear' &&
      !window.confirm(
        r.kind === 'review'
          ? `${removeLabel(r)}? It will no longer show in the app.`
          : `Remove this ${FIELD[r.field] || r.field} from ${r.user?.name || 'this practitioner'}'s ${KIND[r.kind]?.toLowerCase()}?`
      )
    )
      return
    setBusyId(r._id)
    setError('')
    try {
      await axios.put(`${BASE_URL}/admin/content-flags/${r._id}/${action}`)
      await load()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not update this item.')
    } finally {
      setBusyId(null)
    }
  }

  const scanAll = async () => {
    setScanning(true)
    setMsg('')
    setError('')
    try {
      const {data} = await axios.post(`${BASE_URL}/admin/content-flags/scan`)
      setMsg(
        `Checked ${data.checked} profiles, services and classes for flagged words. ${data.open} item(s) need review.` +
          (data.aiStarted ? ' The AI check is also reading everything in the background - refresh in a few minutes for its results.' : '')
      )
      if (status === 'open') await load()
      else setStatus('open')
    } catch (e) {
      setError('The scan failed. Please try again.')
    } finally {
      setScanning(false)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Flagged Content</h3>
        </div>
        <div className='card-toolbar gap-3'>
          <button className='btn btn-sm btn-light-primary' disabled={scanning} onClick={scanAll}>
            {scanning ? 'Checking...' : 'Check all content now'}
          </button>
          <select className='form-select form-select-solid w-200px' value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value='open'>Needs review</option>
            <option value='ok'>Marked OK</option>
            <option value='cleared'>Removed / fixed</option>
          </select>
        </div>
      </div>
      <div className='card-body border-top p-9'>
        <p className='text-muted'>
          Names, bios, services and classes are tidied up automatically when they're saved (ALL CAPS,
          extra spaces, missing capitals). Anything containing a flagged word (English or Spanish)
          appears here within about 10 minutes. It stays live on the app until you act: mark it OK if
          it's fine, or remove the text. If the practitioner edits it themselves, it drops off this list.
        </p>
        <p className='text-muted'>
          <span className='badge badge-light-info me-2'>AI</span>
          The AI check also reads new bios, descriptions and reviews for things a word list can't catch:
          medical cure claims, asking clients to pay outside the app, spam, and offensive or misleading
          content. It only flags - it never changes or hides anything by itself.
        </p>
        {msg && <div className='alert alert-success py-3'>{msg}</div>}
        {error && <div className='alert alert-danger py-3'>{error}</div>}
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-4'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase'>
                <th>Text</th>
                <th>Where</th>
                <th>Person</th>
                <th>Date</th>
                <th className='text-end'></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className='text-muted'>Loading...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className='text-muted'>Nothing here.</td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r._id}>
                    <td style={{maxWidth: 460, whiteSpace: 'pre-wrap'}}>
                      {r.source === 'ai' ? r.text : highlight(r.text, r.words)}
                      {r.source === 'ai' ? (
                        <div className='text-danger fs-7 mt-1'>
                          <span className='badge badge-light-info me-2'>AI</span>
                          {AI_CATEGORY[r.words?.[0]] || r.words?.[0] || 'Flagged'}
                          {r.reason ? ` - ${r.reason}` : ''}
                        </div>
                      ) : (
                        <div className='text-danger fs-7 mt-1'>Flagged: {r.words.join(', ')}</div>
                      )}
                    </td>
                    <td>
                      <div>{KIND[r.kind] || r.kind}</div>
                      <div className='text-muted fs-7'>{FIELD[r.field] || r.field}</div>
                      {r.item && <div className='text-muted fs-7'>{r.item}</div>}
                    </td>
                    <td>
                      <div>{r.user?.name}</div>
                      {r.user?._id && r.kind !== 'review' && (
                        <Link className='fs-7' to={`/edit-text?user=${r.user._id}`}>
                          Edit text
                        </Link>
                      )}
                      <div className='text-muted fs-7'>{r.user?.email}</div>
                      <div className='text-muted fs-7'>{r.user?.town}</div>
                    </td>
                    <td>{new Date(r.updatedAt).toLocaleDateString()}</td>
                    <td className='text-end text-nowrap'>
                      {r.status === 'open' && (
                        <>
                          <button className='btn btn-sm btn-light me-2' disabled={busyId === r._id} onClick={() => act(r, 'ok')}>
                            It's OK
                          </button>
                          {!NAME_FIELDS.includes(r.field) && (
                            <button className='btn btn-sm btn-light-danger' disabled={busyId === r._id} onClick={() => act(r, 'clear')}>
                              {removeLabel(r)}
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default FlaggedContent
